// Agenda du club (D-034) : tournois, lotos, assemblée générale, stages… Les membres
// s'inscrivent (avec le nombre de personnes) ; le bureau crée l'événement, suit les
// inscrits, ses besoins en bénévoles et ses appels aux partenaires, et l'annonce.
import {
  acceptEventAsk, addEventAsk, addEventSlot, cancelClubEvent, clubAgenda, deleteEventAsk, deleteEventSlot, eventAsks,
  eventSignups, eventSlots, leaveClubEvent, removeEventSignup, reopenEventAsk, saveClubEvent, signUpClubEvent,
  type AgendaEvent, type ClubEventDraft,
} from '../lib/api';
import { EVENT_KIND_LABEL, placesLabel, placesLeft, SIGNUP_LABEL, type ClubEventKind, type SignupMode } from '../domain/club-events';
import { displayName } from '../domain/labels';
import { localDate } from '../domain/schedule';
import { me, modOn, type Ctx } from './context';
import { tournamentCan, tournamentList, type TListItem } from '../lib/tournoi-api';
import { premier, time } from './format';
import { $, $$, confirmTwice, html, mount, openSheet, toast, armed } from './dom';
import { tv } from './vous';
import { openCompose } from './announcements';

const NAME_KEY = 'arceau.benevole.prenom';   // même prénom retenu que pour le bénévolat
const savedName = () => { try { return localStorage.getItem(NAME_KEY) ?? ''; } catch { return ''; } };
const saveName = (n: string) => { try { localStorage.setItem(NAME_KEY, n); } catch { /* navigation privée */ } };
const dayLong = (iso: string) => premier(new Date(iso).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }));
const when = (e: { starts_at: string; ends_at: string | null }) => {
  const s = new Date(e.starts_at), en = e.ends_at ? new Date(e.ends_at) : null;
  const sameDay = en && localDate(en) === localDate(s);
  return `${dayLong(e.starts_at)} · ${time(s)}${en ? (sameDay ? ` – ${time(en)}` : ` → ${dayLong(e.ends_at!)} ${time(en)}`) : ''}`;
};
const tile = (iso: string) => {
  const d = new Date(iso);
  return html`<span class="ce-tile" aria-hidden="true"><b>${d.getDate()}</b><small>${d.toLocaleDateString('fr-FR', { month: 'short' })}</small></span>`;
};
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString(); };

export async function renderClubAgenda(root: HTMLElement, ctx: Ctx): Promise<void> {
  const club = ctx.club;
  if (!club) { mount(root, html`<div class="empty"><p class="empty-title">Aucun club.</p></div>`); return; }
  const admin = ctx.adminOf.some(c => c.id === club.id);
  const tourOn = modOn(ctx, 'tournois');
  const [list, tours, canTour] = await Promise.all([clubAgenda(club.id, startOfToday()).catch(() => [] as AgendaEvent[]),
    tourOn ? tournamentList(club.id).catch(() => [] as TListItem[]) : Promise.resolve([] as TListItem[]),
    tourOn ? tournamentCan(club.id).catch(() => false) : Promise.resolve(false)]);
  const tourOf = new Map(tours.filter(x => x.club_event_id).map(x => [x.club_event_id!, x]));
  const reload = () => renderClubAgenda(root, ctx);
  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">${club.name}</p><h2>Agenda du club</h2></header>
      ${admin || canTour ? html`<div class="btn-row ce-new">${admin ? html`<button type="button" class="btn btn-primary btn-sm" id="b-new">+ Événement</button>` : ''}
        ${canTour ? html`<a class="btn ${admin ? 'btn-ghost' : 'btn-primary'} btn-sm" href="#/tournoi/nouveau" data-guide="ag-tournoi">+ Tournoi</a>` : ''}</div>` : ''}
      ${list.length ? html`<div class="ce-list">${list.map(e => eventCard(e, admin, tourOf.get(e.id) ?? null))}</div>`
        : html`<div class="empty"><p class="empty-title">Rien de prévu pour l'instant.</p>${admin ? html`<p class="muted">Tournoi, loto, assemblée générale, stage : crée le premier événement.</p>` : ''}</div>`}
    </section>`);
  $(root, '#b-new')?.addEventListener('click', () => openEditor(ctx, club.id, null, reload));
  wireCards(root, ctx, list, admin, reload);
}

function eventCard(e: AgendaEvent, admin: boolean, tour: TListItem | null = null) {
  const left = placesLeft(e.capacity, e.taken);
  return html`
    <article class="card ce-card ${e.cancelled ? 'ce-off' : ''}" data-ev="${e.id}">
      ${tile(e.starts_at)}
      <div class="ce-main">
        <span class="eyebrow">${EVENT_KIND_LABEL[e.kind]}${e.is_public ? ' · public' : ''}</span>
        <strong class="ce-title">${e.title}</strong>
        <span class="muted small">${when(e)}${e.location ? ` · ${e.location}` : ''}</span>
        ${e.partners.length ? html`<span class="small">Avec ${e.partners.join(', ')}</span>` : ''}
        ${e.description ? html`<p class="small ce-desc">${e.description}</p>` : ''}
        ${e.cancelled ? html`<span class="chip chip-ko">Annulé</span>` : e.signup === 'none' ? '' : html`
          <div class="ce-signup">
            <span class="muted small">${placesLabel(e.capacity, e.taken)}</span>
            ${e.mine ? html`<span class="chip chip-ok">Inscrit · ${e.mine} ${e.mine > 1 ? 'personnes' : 'personne'}</span>
                <button type="button" class="btn btn-ghost btn-sm" data-join="${e.id}">Modifier</button>
                <button type="button" class="btn btn-ghost btn-sm" data-leave="${e.id}">Me désinscrire</button>`
              : left === 0 ? html`<span class="chip chip-mute">Complet</span>`
              : html`<button type="button" class="btn btn-primary btn-sm" data-guide="ag-join" data-join="${e.id}">Je viens</button>`}
          </div>`}
        ${tour ? html`<div class="btn-row">${tour.org ? html`<a class="btn btn-primary btn-sm" href="#/tournoi/${tour.id}${tour.live ? '/direct' : ''}" data-guide="ag-tournoi-tab">${tour.live ? '● Tournoi en direct' : 'Tournoi'}</a>` : ''}
          ${tour.status !== 'draft' || tour.announced ? html`<a class="btn btn-ghost btn-sm" href="/t/${tour.slug}" target="_blank" rel="noopener">${tour.status === 'draft' ? 'La page du tournoi' : 'Suivre le tournoi'}</a>` : tour.org ? html`<span class="chip chip-mute">Brouillon</span>` : ''}</div>` : ''}
        ${admin ? html`<button type="button" class="btn btn-ghost btn-sm ce-manage" data-manage="${e.id}">Gérer</button>` : ''}
      </div>
    </article>`;
}

function wireCards(root: HTMLElement, ctx: Ctx, list: AgendaEvent[], admin: boolean, reload: () => void) {
  const find = (id: string) => list.find(e => e.id === id)!;
  for (const b of $$<HTMLButtonElement>(root, '[data-join]')) b.addEventListener('click', () => openJoin(ctx, find(b.dataset.join!), reload));
  for (const b of $$<HTMLButtonElement>(root, '[data-leave]')) {
    b.addEventListener('click', async () => {
      try { await leaveClubEvent(b.dataset.leave!); toast('Désinscription faite'); reload(); }
      catch { toast('Impossible pour le moment.', 'ko'); }
    });
  }
  if (admin) for (const b of $$<HTMLButtonElement>(root, '[data-manage]')) b.addEventListener('click', () => openManage(ctx, find(b.dataset.manage!), reload));
}

/** « Je viens » : combien de personnes, sous quel prénom. */
function openJoin(ctx: Ctx, e: AgendaEvent, onDone: () => void) {
  const self = me(ctx);
  const known = self ? displayName(self) : savedName();
  const left = placesLeft(e.capacity, e.taken);
  const max = Math.min(20, left == null ? 20 : left + e.mine);
  openSheet(e.title, html`
    <form class="form" id="f-join" novalidate>
      <p class="muted">${when(e)}${e.location ? ` · ${e.location}` : ''}</p>
      <label class="field"><span>Prénom</span><input name="name" value="${known}" maxlength="80" autocomplete="given-name" required></label>
      <label class="field"><span>Nombre de personnes (toi compris)</span><input name="people" type="number" inputmode="numeric" min="1" max="${max}" value="${e.mine || 1}"></label>
      ${left != null ? html`<p class="hint">${left} ${left > 1 ? 'places restantes' : 'place restante'}.</p>` : ''}
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">${e.mine ? 'Enregistrer' : 'Je viens'}</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-join')!;
    f.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      const d = new FormData(f);
      const name = String(d.get('name') ?? '').trim();
      const people = Number(d.get('people'));
      if (!name) { $(f, '.form-msg')!.textContent = 'Indique ton prénom.'; return; }
      if (!Number.isInteger(people) || people < 1 || people > max) { $(f, '.form-msg')!.textContent = `Entre 1 et ${max} personnes.`; return; }
      if (!self) saveName(name);
      try { await signUpClubEvent(e.id, name, people); toast(e.mine ? 'Inscription modifiée' : 'Inscription enregistrée'); close(); onDone(); }
      catch (err) { $(f, '.form-msg')!.textContent = /complet/.test(String((err as Error).message)) ? 'Plus assez de places.' : 'Inscription impossible. Réessaie.'; }
    });
  });
}

// --- Bureau ----------------------------------------------------------------------------
function toLocalParts(iso: string | null) {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  return { date: localDate(d), time: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` };
}

function openEditor(ctx: Ctx, clubId: string, e: AgendaEvent | null, onDone: () => void) {
  const s = toLocalParts(e?.starts_at ?? null), en = toLocalParts(e?.ends_at ?? null);
  let signup: SignupMode = e?.signup ?? 'members';
  openSheet(e ? 'Modifier l’événement' : 'Nouvel événement', html`
    <form class="form" id="f-ce" novalidate>
      <label class="field"><span>Titre</span><input name="title" value="${e?.title ?? ''}" maxlength="120" required placeholder="Tournoi de Noël U11 – U13"></label>
      <label class="field"><span>Type</span><select name="kind">${(Object.keys(EVENT_KIND_LABEL) as ClubEventKind[]).map(k => html`<option value="${k}" ${k === (e?.kind ?? 'tournoi') ? 'selected' : ''}>${EVENT_KIND_LABEL[k]}</option>`)}</select></label>
      <div class="row-2">
        <label class="field"><span>Date</span><input name="date" type="date" value="${s.date}" required></label>
        <label class="field"><span>Début</span><input name="start" type="time" value="${s.time || '10:00'}" required></label>
      </div>
      <div class="row-2">
        <label class="field"><span>Fin (facultatif)</span><input name="end" type="time" value="${en.time}"></label>
        <label class="field"><span>Places (facultatif)</span><input name="capacity" type="number" inputmode="numeric" min="1" max="5000" value="${e?.capacity ?? ''}"></label>
      </div>
      <label class="field"><span>Lieu</span><input name="location" value="${e?.location ?? ''}" maxlength="160" placeholder="Gymnase municipal"></label>
      <label class="field"><span>Présentation (facultatif)</span><textarea name="description" rows="3" maxlength="1500">${e?.description ?? ''}</textarea></label>
      <label class="check"><input type="checkbox" name="is_public" ${e?.is_public ? 'checked' : ''}><span><strong>Sur la page publique du club</strong> <span class="muted small">« Agenda du club »</span></span></label>
      <div class="field"><span>Inscriptions</span>
        <div class="seg seg-wrap" role="radiogroup" data-seg="signup">${(Object.keys(SIGNUP_LABEL) as SignupMode[]).map(m => html`
          <button type="button" role="radio" data-v="${m}" aria-pressed="${String(m === signup)}">${SIGNUP_LABEL[m]}</button>`)}</div></div>
      <p class="hint">« Ouvert au public » : les visiteurs s'inscrivent depuis la page publique (l'événement doit y figurer).</p>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">${e ? 'Enregistrer' : 'Créer l’événement'}</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-ce')!;
    const msg = (t: string) => { $(f, '.form-msg')!.textContent = t; };
    $(sheet, '[data-seg="signup"]')!.querySelectorAll<HTMLButtonElement>('[data-v]').forEach(b => b.addEventListener('click', () => {
      signup = b.dataset.v as SignupMode;
      $(sheet, '[data-seg="signup"]')!.querySelectorAll('[data-v]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      if (signup === 'public') (f.elements.namedItem('is_public') as HTMLInputElement).checked = true;
    }));
    f.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      msg('');
      const d = new FormData(f);
      const v = (k: string) => String(d.get(k) ?? '').trim();
      if (!v('title')) { msg('Indique un titre.'); return; }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(v('date')) || !/^\d{2}:\d{2}$/.test(v('start'))) { msg('Indique la date et l’heure de début.'); return; }
      const starts = new Date(`${v('date')}T${v('start')}`);
      const ends = v('end') ? new Date(`${v('date')}T${v('end')}`) : null;
      if (ends && ends <= starts) { msg('La fin doit être après le début.'); return; }
      const cap = v('capacity') ? Number(v('capacity')) : null;
      if (cap != null && (!Number.isInteger(cap) || cap < 1)) { msg('Nombre de places invalide.'); return; }
      const isPublic = d.get('is_public') === 'on';
      if (signup === 'public' && !isPublic) { msg('Inscription du public : coche « Sur la page publique ».'); return; }
      const draft: ClubEventDraft = {
        id: e?.id, club_id: clubId, title: v('title'), kind: v('kind') as ClubEventKind, starts_at: starts.toISOString(),
        ends_at: ends ? ends.toISOString() : null, location: v('location') || null, description: v('description') || null,
        is_public: isPublic, signup, capacity: cap,
      };
      try { await saveClubEvent(draft); toast(e ? 'Événement modifié' : 'Événement créé'); close(); onDone(); }
      catch { msg('Enregistrement impossible. Réessaie.'); }
    });
  });
}

/** Le bureau : inscrits, bénévoles, partenaires, annonce, annulation. */
export async function openManage(ctx: Ctx, e: AgendaEvent, onDone: () => void) {
  const club = ctx.club!;
  const [signups, slots, asks] = await Promise.all([eventSignups(e.id), eventSlots(e.id), eventAsks(e.id)]);
  const people = signups.reduce((a, x) => a + x.people, 0);
  const ASK_STATUS = { ouvert: 'En attente d’un partenaire', propose: 'propose sa participation', accepte: 'participe' } as const;
  openSheet(e.title, html`
    <div class="ce-manage-body">
      <p class="muted">${EVENT_KIND_LABEL[e.kind]} · ${when(e)}${e.location ? ` · ${e.location}` : ''}</p>
      <div class="btn-row">
        <button type="button" class="btn btn-ghost btn-sm" id="m-edit">Modifier</button>
        <button type="button" class="btn btn-ghost btn-sm" id="m-announce">Annoncer aux membres</button>
      </div>

      <section class="sheet-sec">
        <h4>Inscrits · ${people} ${people > 1 ? 'personnes' : 'personne'}${e.capacity ? ` sur ${e.capacity}` : ''}</h4>
        ${signups.length ? html`<ul class="ce-rows">${signups.map(x => html`
          <li><span><strong>${x.display_name}</strong> · ${x.people} ${x.people > 1 ? 'pers.' : 'pers.'}${x.user_id ? '' : html` <span class="chip chip-mute">public</span>`}
            ${x.email || x.phone ? html`<br><span class="muted small">${[x.email, x.phone].filter(Boolean).join(' · ')}</span>` : ''}</span>
            <button type="button" class="sheet-x" data-unsign="${x.id}" aria-label="Retirer ${x.display_name}">×</button></li>`)}</ul>`
          : html`<p class="muted small">${e.signup === 'none' ? 'Pas d’inscription pour cet événement.' : 'Personne pour l’instant.'}</p>`}
      </section>

      <section class="sheet-sec">
        <h4>Bénévoles</h4>
        ${slots.length ? html`<ul class="ce-rows">${slots.map(x => html`
          <li><span>${x.label} · ${x.needed} ${x.needed > 1 ? 'personnes' : 'personne'}</span>
            <button type="button" class="sheet-x" data-unslot="${x.id}" aria-label="Retirer ${x.label}">×</button></li>`)}</ul>` : ''}
        <form class="inline-form" id="f-slot" novalidate>
          <input name="label" maxlength="60" placeholder="Buvette, installation, table de marque…" aria-label="Besoin">
          <input name="needed" type="number" min="1" max="20" value="2" aria-label="Nombre" class="ce-num">
          <button class="btn btn-ghost btn-sm" type="submit">Ajouter</button>
        </form>
        <p class="hint">Les besoins apparaissent avec ceux des matchs : chacun prend un créneau en un geste.</p>
      </section>

      <section class="sheet-sec">
        <h4>Appels aux partenaires</h4>
        ${asks.length ? html`<ul class="ce-rows">${asks.map(a => html`
          <li><span><strong>${a.label}</strong>${a.counterpart ? html`<br><span class="muted small">En échange : ${a.counterpart}</span>` : ''}
            <br><span class="small ${a.status === 'accepte' ? 'ce-ok' : a.status === 'propose' ? 'ce-warn' : 'muted'}">${a.partners?.name ? `${a.partners.name} ` : ''}${ASK_STATUS[a.status]}</span></span>
            <span class="btn-row">
              ${a.status === 'propose' ? html`<button type="button" class="btn btn-primary btn-sm" data-accept="${a.id}">Accepter</button>
                <button type="button" class="btn btn-ghost btn-sm" data-reopen="${a.id}">Refuser</button>` : ''}
              ${a.status === 'accepte' ? html`<button type="button" class="btn btn-ghost btn-sm" data-reopen="${a.id}">Retirer</button>` : ''}
              <button type="button" class="sheet-x" data-unask="${a.id}" aria-label="Supprimer l’appel">×</button>
            </span></li>`)}</ul>` : ''}
        <form class="form" id="f-ask" novalidate>
          <input name="label" maxlength="80" placeholder="Coupes et médailles, lots du loto…" aria-label="Ce dont le club a besoin">
          <input name="counterpart" maxlength="200" placeholder="En échange : votre logo sur l’affiche et la page de l’événement" aria-label="Contrepartie">
          <button class="btn btn-ghost btn-sm" type="submit">Lancer l’appel</button>
        </form>
        <p class="hint">Les partenaires le voient dans leur espace et répondent « Je participe » ; une fois accepté, leur nom apparaît sur l’événement.</p>
      </section>

      ${e.cancelled ? '' : html`<button type="button" class="btn btn-danger-ghost" id="m-cancel">Annuler l’événement</button>`}
    </div>`, (sheet, close) => {
    const again = async (p: Promise<unknown>, ok: string) => {
      try { await p; toast(ok); close(); void openManage(ctx, e, onDone); }
      catch { toast('Impossible pour le moment.', 'ko'); }
    };
    $(sheet, '#m-edit')!.addEventListener('click', () => { close(); openEditor(ctx, club.id, e, onDone); });
    $(sheet, '#m-announce')!.addEventListener('click', () => {
      close();
      openCompose(ctx, () => undefined, {
        team: null, title: `${EVENT_KIND_LABEL[e.kind]} : ${e.title}`,
        body: [`${when(e).charAt(0).toUpperCase()}${when(e).slice(1)}${e.location ? `, ${e.location}` : ''}.`, e.description ?? '',
          e.signup !== 'none' ? 'Inscris-toi dans l’agenda du club.' : ''].filter(Boolean).join('\n\n'),
        link: '#/agenda',
      });
    });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-unsign]')) b.addEventListener('click', () => { if (armed(b)) void again(removeEventSignup(b.dataset.unsign!), 'Inscription retirée'); });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-unslot]')) b.addEventListener('click', () => { if (armed(b)) void again(deleteEventSlot(b.dataset.unslot!), 'Besoin retiré'); });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-accept]')) b.addEventListener('click', () => void again(acceptEventAsk(b.dataset.accept!), 'Partenaire accepté'));
    for (const b of $$<HTMLButtonElement>(sheet, '[data-reopen]')) b.addEventListener('click', () => void again(reopenEventAsk(b.dataset.reopen!), 'Appel rouvert'));
    for (const b of $$<HTMLButtonElement>(sheet, '[data-unask]')) b.addEventListener('click', () => { if (armed(b)) void again(deleteEventAsk(b.dataset.unask!), 'Appel supprimé'); });
    const fs = $<HTMLFormElement>(sheet, '#f-slot')!;
    fs.addEventListener('submit', (ev) => {
      ev.preventDefault();
      const d = new FormData(fs);
      const label = String(d.get('label') ?? '').trim(), needed = Number(d.get('needed'));
      if (!label || !Number.isInteger(needed) || needed < 1 || needed > 20) { toast('Indique le besoin et le nombre (1 à 20).', 'ko'); return; }
      void again(addEventSlot(club.id, e.id, label, needed, slots.length), 'Besoin ajouté');
    });
    const fa = $<HTMLFormElement>(sheet, '#f-ask')!;
    fa.addEventListener('submit', (ev) => {
      ev.preventDefault();
      const d = new FormData(fa);
      const label = String(d.get('label') ?? '').trim();
      if (!label) { toast('Indique ce dont le club a besoin.', 'ko'); return; }
      void again(addEventAsk(e.id, label, String(d.get('counterpart') ?? '').trim() || null), 'Appel lancé aux partenaires');
    });
    const cancel = $<HTMLButtonElement>(sheet, '#m-cancel');
    if (cancel) confirmTwice(cancel, 'Confirmer l’annulation', async () => {
      try { await cancelClubEvent(e.id); toast('Événement annulé'); close(); }
      catch { toast('Impossible pour le moment.', 'ko'); }
    });
  }, onDone);
}

/** Accueil : le prochain événement du club (6 semaines), avec l'inscription en un geste. */
export async function renderClubEventCard(el: HTMLElement, ctx: Ctx): Promise<void> {
  if (!ctx.club) return;
  const soon = new Date(Date.now() + 42 * 86_400_000).toISOString();
  const next = (await clubAgenda(ctx.club.id, new Date().toISOString()).catch(() => [] as AgendaEvent[]))
    .find(e => !e.cancelled && e.starts_at <= soon);
  if (!next) { mount(el, html``); return; }
  const left = placesLeft(next.capacity, next.taken);
  mount(el, html`
    <a class="card ce-home" href="#/agenda">
      ${tile(next.starts_at)}
      <span class="ce-main">
        <span class="eyebrow">Agenda du club · ${EVENT_KIND_LABEL[next.kind]}</span>
        <strong class="ce-title">${next.title}</strong>
        <span class="muted small">${when(next)}${next.location ? ` · ${next.location}` : ''}</span>
        ${next.signup === 'none' ? '' : html`<span class="small">${next.mine ? `Inscription faite (${next.mine})` : left === 0 ? 'Complet' : tv(ctx, 'Inscris-toi en un geste', 'Inscrivez-vous en un geste')}</span>`}
      </span>
      <span class="occ-go" aria-hidden="true">›</span>
    </a>`);
}
